// Versão: 1.01
// Data..: 2026-10-13
// Sons de alerta sintetizados via Web Audio API (sem arquivos, sem custo, sem direitos).
// Opções curtas e longas para o usuário escolher. A preferência é salva em localStorage.
// v1.01: playSound aceita volume (0..1) aplicado ao pico de cada nota; novos sons mais
//        longos ('chime-long' e 'melody').

// Identificador de cada som disponível.
export type SoundId = 'chime' | 'ding' | 'blip' | 'pop' | 'alert' | 'chime-long' | 'melody';

export interface SoundOption {
  id: SoundId;
  label: string;
}

// Catálogo exibido na tela de preferências (5 curtos + 2 longos).
export const SOUND_OPTIONS: SoundOption[] = [
  { id: 'chime', label: 'Sino suave' },
  { id: 'ding', label: 'Ding' },
  { id: 'blip', label: 'Blip curto' },
  { id: 'pop', label: 'Pop' },
  { id: 'alert', label: 'Alerta duplo' },
  { id: 'chime-long', label: 'Sino longo' },
  { id: 'melody', label: 'Melodia' },
];

// AudioContext único e preguiçoso (criado no primeiro uso, após interação do usuário).
let ctx: AudioContext | null = null;
function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  // Navegadores suspendem o contexto até haver interação; retomamos quando possível.
  if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
  return ctx;
}

// Toca uma nota simples (onda + envelope de volume) no tempo indicado. O pico é
// multiplicado pelo volume (0..1) do usuário; abaixo do mínimo audível, usa o piso.
function playNote(
  audio: AudioContext,
  freq: number,
  startAt: number,
  duration: number,
  type: OscillatorType = 'sine',
  peak = 0.18,
  volume = 1,
): void {
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  // Pico efetivo após aplicar o volume do usuário (mantém acima de 0 para o ramp).
  const effectivePeak = Math.max(0.0002, peak * volume);
  // Envelope: ataque rápido e decaimento exponencial (evita clique/estalo).
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(effectivePeak, startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(gain).connect(audio.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
}

// Toca o som escolhido com o volume informado (0..1). Falhas (ex.: autoplay
// bloqueado) são silenciosas.
export function playSound(id: SoundId, volume = 1): void {
  try {
    const audio = getContext();
    if (!audio) return;
    // Garante o volume dentro de 0..1 antes de propagar às notas.
    const vol = Math.min(1, Math.max(0, volume));
    const t = audio.currentTime + 0.01;
    switch (id) {
      case 'chime':
        playNote(audio, 880, t, 0.5, 'sine', 0.16, vol);
        playNote(audio, 1320, t + 0.12, 0.5, 'sine', 0.12, vol);
        break;
      case 'ding':
        playNote(audio, 1200, t, 0.45, 'triangle', 0.2, vol);
        break;
      case 'blip':
        playNote(audio, 660, t, 0.12, 'square', 0.12, vol);
        break;
      case 'pop':
        playNote(audio, 420, t, 0.18, 'sine', 0.22, vol);
        break;
      case 'alert':
        playNote(audio, 988, t, 0.14, 'square', 0.14, vol);
        playNote(audio, 988, t + 0.2, 0.14, 'square', 0.14, vol);
        break;
      // Sino longo: arpejo ascendente com cauda ressonante (~1,6s).
      case 'chime-long':
        playNote(audio, 660, t, 0.6, 'sine', 0.14, vol);
        playNote(audio, 880, t + 0.25, 0.6, 'sine', 0.14, vol);
        playNote(audio, 1100, t + 0.5, 0.7, 'sine', 0.12, vol);
        playNote(audio, 1320, t + 0.8, 0.8, 'sine', 0.1, vol);
        break;
      // Melodia: sequência de 5 notas com timbre triangular (~1,8s).
      case 'melody':
        playNote(audio, 523, t, 0.3, 'triangle', 0.16, vol);
        playNote(audio, 659, t + 0.3, 0.3, 'triangle', 0.16, vol);
        playNote(audio, 784, t + 0.6, 0.3, 'triangle', 0.16, vol);
        playNote(audio, 1047, t + 0.9, 0.4, 'triangle', 0.16, vol);
        playNote(audio, 784, t + 1.3, 0.5, 'triangle', 0.14, vol);
        break;
      default:
        playNote(audio, 880, t, 0.3, 'sine', 0.16, vol);
    }
  } catch {
    // som é acessório: nunca quebra a UI
  }
}
